import clsx from 'clsx';
import {
  formatRelativeTime,
  incidentTone,
  worstIncidentTone,
} from '../lib/incident-helpers';
import type { Incident } from '../lib/incidents-api';
import { statusTone, type Tone } from '../lib/status-mapping';
import type { ComponentStatus } from '../lib/status-types';
import { Frame } from './Frame';
import { IncidentStatusBadge } from './IncidentStatusBadge';
import { SectionHeading } from './SectionHeading';

const HERO_PADDING =
  'px-4 pb-16 pt-[72px] min-[768px]:px-[42px] min-[768px]:pb-24 min-[768px]:pt-24 min-[1200px]:px-[72px] min-[1200px]:pb-[112px] min-[1200px]:pt-[120px]';

// `statusTone` never yields `major` (that band belongs to the suite pill);
// the key exists only to keep the record total.
const HERO_STATEMENT: Record<Tone, string> = {
  ok: 'All systems operational',
  warn: 'Some systems are experiencing degraded performance',
  major: 'Some systems are experiencing a partial outage',
  down: 'Some systems are experiencing a partial outage',
  none: 'Health data unavailable',
};

/**
 * The page's one-glance answer, in the toncenter.com hero pattern (DESIGN.md
 * §8): accent eyebrow + large heading. The heading IS the status statement;
 * a status dot in front of it carries the tone. An open incident takes the
 * hero over — it matters more than the automated roll-up.
 */
export function Hero({
  status,
  updatedAt,
  loading,
  activeIncidents,
  onSelectIncident,
}: {
  status: ComponentStatus;
  /** `statusComputedAt` of the freshest component, ISO-8601, or null. */
  updatedAt: string | null;
  loading: boolean;
  activeIncidents: readonly Incident[];
  onSelectIncident: (id: number) => void;
}) {
  if (loading) {
    return (
      <Frame
        as="section"
        lines="bottom"
        aria-busy="true"
        className={HERO_PADDING}
      >
        <div className="flex flex-col gap-4 min-[768px]:gap-[18px]">
          <div className="skeleton-shine h-4 w-40 rounded" />
          <div className="skeleton-shine h-8 w-[min(560px,100%)] rounded min-[768px]:h-[52px]" />
          <div className="skeleton-shine h-5 w-[min(420px,80%)] rounded" />
        </div>
      </Frame>
    );
  }

  if (activeIncidents.length > 0) {
    const tone = worstIncidentTone(activeIncidents);
    const lead =
      activeIncidents.find((i) => incidentTone(i) === tone) ??
      activeIncidents[0];
    const count = activeIncidents.length;
    const labelTone =
      tone === 'down'
        ? 'text-down-fg'
        : tone === 'warn'
          ? 'text-warn-fg'
          : 'text-info-fg';
    return (
      <Frame
        as="section"
        lines="bottom"
        aria-labelledby="hero-title"
        className={HERO_PADDING}
      >
        <div className="flex max-w-[860px] flex-col items-start gap-8 min-[768px]:gap-10">
          <div className="flex flex-col gap-4">
            <SectionHeading
              variant="hero"
              id="hero-title"
              label={count === 1 ? 'Active incident' : 'Active incidents'}
              labelClassName={labelTone}
              title={
                count === 1 ? lead.title : `${String(count)} active incidents`
              }
            />
            {count === 1 && (
              <div>
                <IncidentStatusBadge status={lead.status} />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              onSelectIncident(lead.id);
            }}
            className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-full bg-accent-fill px-[18px] py-2 text-[16px] font-semibold leading-6 text-white transition-transform duration-150 active:translate-y-px"
          >
            {count === 1 ? 'View incident' : 'View all'}
            <span aria-hidden>→</span>
          </button>
        </div>
      </Frame>
    );
  }

  const tone = statusTone(status);
  // Freshness of the verdict, NOT when anything broke — a bare wall clock
  // next to "partial outage" reads as the incident's start time, which it
  // never was. Relative per DESIGN.md §11.
  const freshness = updatedAt === null ? '' : formatRelativeTime(updatedAt);
  return (
    <Frame
      as="section"
      lines="bottom"
      aria-labelledby="hero-title"
      className={HERO_PADDING}
    >
      <div className="flex max-w-[980px] flex-col gap-4 min-[768px]:gap-5">
        <SectionHeading
          variant="hero"
          id="hero-title"
          label="Toncenter health"
          title={
            <span className="flex items-start gap-[0.3em]">
              {/* Dot sized in em so it tracks the heading's type size and
                  sits on the first line's centre when the statement wraps:
                  (line-height 1.05em − 0.3em) / 2 = 0.375em. */}
              <span
                aria-hidden
                className="relative mt-[0.375em] inline-flex h-[0.3em] w-[0.3em] flex-shrink-0"
              >
                <HeroDot tone={tone} />
              </span>
              <span className={tone === 'none' ? 'text-ink-3' : undefined}>
                {HERO_STATEMENT[tone]}
              </span>
            </span>
          }
        />
        <p className="m-0 max-w-[599px] text-[16px] leading-[1.6] text-ink-3 min-[768px]:text-[18px]">
          Results of automated checks against TON Center APIs.
          {/* Own line: glued to the sentence it wrapped mid-phrase
              ("Updated just / now.") at most widths. */}
          {freshness !== '' && (
            <span className="block [font-feature-settings:'tnum']">
              Updated {freshness}.
            </span>
          )}
        </p>
      </div>
    </Frame>
  );
}

/** The hero's status dot: calm pulse when ok, ping ring otherwise. */
function HeroDot({ tone }: { tone: Tone }) {
  const color =
    tone === 'ok'
      ? 'bg-ok'
      : tone === 'warn'
        ? 'bg-warn'
        : tone === 'major'
          ? 'bg-major'
          : tone === 'down'
            ? 'bg-down'
            : 'bg-line-strong';
  return (
    <>
      {tone !== 'ok' && tone !== 'none' && (
        <span
          className={clsx(
            'absolute inset-0 animate-ping rounded-full opacity-60',
            color,
          )}
        />
      )}
      <span className={clsx('relative h-full w-full rounded-full', color)} />
    </>
  );
}
